/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
    Ad,
    AdPage,
    AdPlacement,
    AdSettings,
    AdImpression,
    AdClick,
    MOCK_AD_SETTINGS,
    getActiveAdsForPage,
} from '../data/mockAds';
import { useFeatureFlags } from './FeatureFlagContext';

// ============================================================================
// TYPES
// ============================================================================

interface AdContextType {
    settings: AdSettings;
    updateSettings: (settings: Partial<AdSettings>) => void;
    getAdsForPage: (page: AdPage, placement: AdPlacement, category?: string) => Ad[];
    getCurrentAd: (page: AdPage, placement: AdPlacement) => Ad | null;
    rotateAd: (page: AdPage, placement: AdPlacement) => void;
    trackImpression: (ad: Ad, page: AdPage, placement: AdPlacement) => void;
    trackClick: (ad: Ad, page: AdPage, placement: AdPlacement, action: string) => void;
    impressions: AdImpression[];
    clicks: AdClick[];
    shouldShowAds: (page: AdPage) => boolean;
    isDelayPassed: boolean;
}

const AdContext = createContext<AdContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const AdProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isEnabled, flags } = useFeatureFlags();
    
    const [settings, setSettings] = useState<AdSettings>(MOCK_AD_SETTINGS);
    
    // Sync settings with feature flags from API
    useEffect(() => {
        setSettings(prev => ({
            ...prev,
            globalEnabled: isEnabled('advertisements_global') ?? prev.globalEnabled,
            pageSettings: {
                community: isEnabled('advertisements_community') ?? prev.pageSettings.community,
                my_trips: isEnabled('advertisements_my_trips') ?? prev.pageSettings.my_trips,
                trip_details: isEnabled('advertisements_trip_details') ?? prev.pageSettings.trip_details,
                my_photos: isEnabled('advertisements_my_photos') ?? prev.pageSettings.my_photos,
                destinations: isEnabled('advertisements_destinations') ?? prev.pageSettings.destinations,
            },
            tierSettings: {
                premium: isEnabled('advertisements_premium') ?? prev.tierSettings.premium,
                standard: isEnabled('advertisements_standard') ?? prev.tierSettings.standard,
                featured: isEnabled('advertisements_featured') ?? prev.tierSettings.featured,
            },
        }));
    }, [flags, isEnabled]);
    
    const [adIndices, setAdIndices] = useState<Record<string, number>>({});
    const [impressions, setImpressions] = useState<AdImpression[]>([]);
    const [clicks, setClicks] = useState<AdClick[]>([]);
    const sessionId = useRef<string>(generateSessionId());
    const impressionCounts = useRef<Record<string, number>>({});
    const [isDelayPassed, setIsDelayPassed] = useState(false);
    const rotationTimers = useRef<Record<string, NodeJS.Timeout>>({});
    
    // Delay timer
    useEffect(() => {
        const timer = setTimeout(() => setIsDelayPassed(true), settings.delayBeforeFirstAd * 1000);
        return () => clearTimeout(timer);
    }, [settings.delayBeforeFirstAd]);
    
    const isGlobalEnabled = useCallback(() => {
        const globalFlag = isEnabled('advertisements_global');
        if (globalFlag === undefined) return settings.globalEnabled;
        return globalFlag;
    }, [isEnabled, settings.globalEnabled]);
    
    const shouldShowAds = useCallback((page: AdPage): boolean => {
        if (!isGlobalEnabled()) return false;
        if (!isDelayPassed) return false;
        const pageFlag = isEnabled(`advertisements_${page}`);
        if (pageFlag !== undefined) return pageFlag;
        return settings.pageSettings[page] ?? false;
    }, [isGlobalEnabled, isDelayPassed, isEnabled, settings.pageSettings]);
    
    const updateSettings = useCallback((newSettings: Partial<AdSettings>) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
    }, []);
    
    const getAdsForPage = useCallback((page: AdPage, placement: AdPlacement, category?: string): Ad[] => {
        let ads = getActiveAdsForPage(page, placement, settings);
        if (category) ads = ads.filter(ad => ad.category === category);
        ads = ads.filter(ad => (impressionCounts.current[ad.id] || 0) < settings.maxImpressionsPerAdPerSession);
        return ads;
    }, [settings]);
    
    const getCurrentAd = useCallback((page: AdPage, placement: AdPlacement): Ad | null => {
        const key = `${page}-${placement}`;
        const ads = getAdsForPage(page, placement);
        if (ads.length === 0) return null;
        const index = adIndices[key] ?? 0;
        return ads[index % ads.length];
    }, [getAdsForPage, adIndices]);
    
    const rotateAd = useCallback((page: AdPage, placement: AdPlacement) => {
        const key = `${page}-${placement}`;
        const ads = getAdsForPage(page, placement);
        if (ads.length === 0) return;
        setAdIndices(prev => ({ ...prev, [key]: ((prev[key] ?? 0) + 1) % ads.length }));
    }, [getAdsForPage]);
    
    const trackImpression = useCallback((ad: Ad, page: AdPage, placement: AdPlacement) => {
        const impression: AdImpression = {
            id: generateId(), adId: ad.id, campaignId: ad.campaignId, partnerId: ad.partnerId,
            userId: getUserId(), sessionId: sessionId.current, page, placement, timestamp: new Date().toISOString(),
        };
        setImpressions(prev => [...prev, impression]);
        impressionCounts.current[ad.id] = (impressionCounts.current[ad.id] || 0) + 1;
        console.log('[Ad Impression]', { adId: ad.id, title: ad.title, page, placement, timestamp: impression.timestamp });
    }, []);
    
    const trackClick = useCallback((ad: Ad, page: AdPage, placement: AdPlacement, action: string) => {
        const click: AdClick = {
            id: generateId(), adId: ad.id, campaignId: ad.campaignId, partnerId: ad.partnerId,
            userId: getUserId(), sessionId: sessionId.current, page, placement, action, timestamp: new Date().toISOString(),
        };
        setClicks(prev => [...prev, click]);
        console.log('[Ad Click]', { adId: ad.id, title: ad.title, page, placement, action, timestamp: click.timestamp });
    }, []);
    
    useEffect(() => {
        return () => { Object.values(rotationTimers.current).forEach(timer => clearInterval(timer)); };
    }, []);
    
    return (
        <AdContext.Provider value={{ settings, updateSettings, getAdsForPage, getCurrentAd, rotateAd, trackImpression, trackClick, impressions, clicks, shouldShowAds, isDelayPassed }}>
            {children}
        </AdContext.Provider>
    );
};

export const useAds = (): AdContextType => {
    const context = useContext(AdContext);
    if (!context) throw new Error('useAds must be used within an AdProvider');
    return context;
};

function generateId(): string { return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`; }

function generateSessionId(): string {
    const stored = sessionStorage.getItem('percoro_ad_session');
    if (stored) return stored;
    const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('percoro_ad_session', newId);
    return newId;
}

function getUserId(): string | undefined {
    const token = localStorage.getItem('idToken');
    if (!token) return undefined;
    try { const payload = JSON.parse(atob(token.split('.')[1])); return payload.sub || payload.email; } catch { return undefined; }
}

export type { AdContextType };
